import React, { useState } from 'react'
import toast from 'react-hot-toast'

interface Message {
  id: string
  name: string
  email: string
  subject: string
  message: string
  createdAt: string
  read: boolean
}

const AdminMessagesPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      name: 'Mario Rossi',
      email: 'mario@example.com',
      subject: 'Informazioni sui tour',
      message: 'Vorrei sapere di più sui tour disponibili per il prossimo weekend.',
      createdAt: '2024-08-08T10:00:00Z',
      read: false
    },
    {
      id: '2',
      name: 'Anna Bianchi',
      email: 'anna@example.com',
      subject: 'Prenotazione gruppo',
      message: 'Siamo un gruppo di 8 persone e vorremmo prenotare un tour personalizzato.',
      createdAt: '2024-08-07T15:30:00Z',
      read: true
    },
    {
      id: '3',
      name: 'Luca Verdi',
      email: 'luca@example.com',
      subject: 'Richiesta preventivo',
      message: 'Avrei bisogno di un preventivo per un tour di 3 giorni.',
      createdAt: '2024-08-06T09:15:00Z',
      read: false
    }
  ])

  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all')

  const filteredMessages = messages.filter(message => {
    if (filter === 'unread') return !message.read
    if (filter === 'read') return message.read
    return true
  })

  const markAsRead = (messageId: string) => {
    setMessages(messages.map(msg =>
      msg.id === messageId ? { ...msg, read: true } : msg
    ))
    toast.success('Messaggio segnato come letto')
  }

  const markAsUnread = (messageId: string) => {
    setMessages(messages.map(msg =>
      msg.id === messageId ? { ...msg, read: false } : msg
    ))
    toast.success('Messaggio segnato come non letto')
  }

  const deleteMessage = (messageId: string) => {
    setMessages(messages.filter(msg => msg.id !== messageId))
    toast.success('Messaggio eliminato')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Messaggi di Contatto
            </h3>
            <div className="flex space-x-3">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">Tutti</option>
                <option value="unread">Non letti</option>
                <option value="read">Letti</option>
              </select>
              <button className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700">
                Esporta
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Messages List */}
            <div className="md:col-span-1">
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`p-4 border rounded-lg cursor-pointer ${
                      selectedMessage?.id === message.id
                        ? 'border-primary-500 bg-primary-50'
                        : message.read
                        ? 'border-gray-200 bg-gray-50'
                        : 'border-blue-200 bg-blue-50'
                    }`}
                    onClick={() => setSelectedMessage(message)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 truncate">
                          {message.subject}
                        </h4>
                        <p className="text-sm text-gray-600 truncate">
                          {message.name} - {message.email}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(message.createdAt)}
                        </p>
                      </div>
                      {!message.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full ml-2"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Message Detail */}
            <div className="md:col-span-2">
              {selectedMessage ? (
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {selectedMessage.subject}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Da: {selectedMessage.name} ({selectedMessage.email})
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(selectedMessage.createdAt)}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => selectedMessage.read ? markAsUnread(selectedMessage.id) : markAsRead(selectedMessage.id)}
                        className={`px-3 py-1 rounded text-xs ${
                          selectedMessage.read
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {selectedMessage.read ? 'Segna non letto' : 'Segna letto'}
                      </button>
                      <button
                        onClick={() => deleteMessage(selectedMessage.id)}
                        className="px-3 py-1 rounded text-xs bg-red-100 text-red-800 hover:bg-red-200"
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Messaggio:</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedMessage.message}
                    </p>
                  </div>

                  <div className="mt-4">
                    <button className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700">
                      Rispondi
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center">
                  <p className="text-gray-500">Seleziona un messaggio per visualizzarlo</p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 text-sm text-gray-500">
            Totale messaggi: {filteredMessages.length} 
            {filter === 'unread' && ` (${messages.filter(m => !m.read).length} non letti)`}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminMessagesPage 